import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Copy, Building2, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { order, showPaymentInfo } = location.state || {};
  const [config, setConfig] = useState({ iban: '', company_name: '', important_info: '' });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/config`);
      setConfig(data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiat!`);
  };

  if (!order) {
    return (
      <div className="min-h-screen bg-[#E8E4DD] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-[#999] mb-4">Nu s-a găsit comanda</p>
          <Button onClick={() => navigate('/')} className="accent-button">
            Înapoi la Meniu
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E4DD]">
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <CheckCircle2 className="w-16 h-16 text-[#5C9F5F] mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-[#1F1F1F] mb-2">
            Comanda a fost plasată!
          </h1>
          <p className="text-[#666]">Mulțumim pentru comanda ta</p>
        </div>

        <div data-testid="email-notification-banner" className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-4 mb-5">
          <p className="text-sm text-[#92400E]">
            <span className="font-semibold">Notificare importantă:</span> Intervalul orar selectat este estimativ. Te rugăm să urmărești e-mailul — vei primi o notificare pe e-mail când comanda ta este gata de ridicare!
          </p>
        </div>

        <div className="minimalist-card p-5 mb-5">
          <h3 className="text-base font-semibold text-[#1F1F1F] mb-4">Detalii Comandă</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-[#F0F0F0]">
              <span className="text-[#666]">Nume</span>
              <span className="font-medium text-[#1F1F1F]">{order.customer_name}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#F0F0F0]">
              <span className="text-[#666]">Telefon</span>
              <span className="font-medium text-[#1F1F1F]">{order.customer_phone}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#F0F0F0]">
              <span className="text-[#666]">Ora ridicare</span>
              <span className="font-medium text-[#1F1F1F]">{order.pickup_time}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#F0F0F0]">
              <span className="text-[#666]">Metodă plată</span>
              <span className="font-medium text-[#1F1F1F]">{order.payment_method}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-[#666]">Total</span>
              <span className="font-semibold text-[#7BA05B] text-xl">{order.total_amount.toFixed(2)} RON</span>
            </div>
          </div>
        </div>

        {showPaymentInfo && (
          <div className="minimalist-card p-5 mb-5 border-2 border-[#7BA05B]">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-6 h-6 text-[#7BA05B]" />
              <h3 className="text-base font-semibold text-[#1F1F1F]">Detalii plată transfer bancar</h3>
            </div>
            <p className="text-[#666] mb-4">
              Te rugăm să faci transferul bancar folosind datele de mai jos. Menționează ID-ul comenzii tale în detaliile plății.
            </p>
            
            <div className="bg-[#F8F8F8] rounded-lg p-4 mb-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-[#999] mb-1">IBAN</p>
                  <p className="font-mono font-semibold text-[#1F1F1F] text-base">{config.iban}</p>
                </div>
                <Button
                  data-testid="copy-iban-button"
                  onClick={() => copyToClipboard(config.iban, 'IBAN')}
                  variant="ghost"
                  size="sm"
                  className="hover:bg-white"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="bg-[#F8F8F8] rounded-lg p-4 mb-3">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-[#999] mb-1">Beneficiar</p>
                  <p className="font-semibold text-[#1F1F1F]">{config.company_name}</p>
                </div>
                <Button
                  data-testid="copy-company-button"
                  onClick={() => copyToClipboard(config.company_name, 'Nume companie')}
                  variant="ghost"
                  size="sm"
                  className="hover:bg-white"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="bg-[#F8F8F8] rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-[#999] mb-1">Detalii plată (ID comandă)</p>
                  <p className="font-semibold text-[#1F1F1F]">#{order.id.slice(-8).toUpperCase()}</p>
                </div>
                <Button
                  data-testid="copy-order-id-button"
                  onClick={() => copyToClipboard(`#${order.id.slice(-8).toUpperCase()}`, 'ID comandă')}
                  variant="ghost"
                  size="sm"
                  className="hover:bg-white"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {config.important_info && (
          <div className="minimalist-card p-5 mb-5 bg-[#F8F9FF] border border-[#D9E0FF]">
            <h3 className="text-base font-semibold text-[#1F1F1F] mb-3">ℹ️ Informații Importante</h3>
            <div className="text-[#666] text-sm whitespace-pre-line">
              {config.important_info}
            </div>
          </div>
        )}

        <div className="minimalist-card p-5 mb-5">
          <h3 className="text-base font-semibold text-[#1F1F1F] mb-3">Urmează comanda ta</h3>
          <p className="text-[#666] mb-4">
            Poți verifica statusul comenzii tale accesând link-ul de mai jos:
          </p>
          <Button
            data-testid="track-order-button"
            onClick={() => navigate(`/track/${order.id}`)}
            className="w-full rounded-lg bg-[#5C9F5F] hover:bg-[#4A8F4D] text-white py-3 font-medium"
          >
            Vezi Status Comandă
          </Button>
        </div>

        <Button
          data-testid="back-home-button"
          onClick={() => navigate('/')}
          variant="outline"
          className="w-full rounded-lg border-[#E8E8E8] hover:bg-[#F8F8F8] py-3 font-medium"
        >
          <Home className="w-5 h-5 mr-2" />
          Înapoi la Meniu
        </Button>
      </main>
    </div>
  );
};

export default OrderConfirmation;